import { useQuery } from '@tanstack/react-query';
import React from 'react';
import { Redirect, Route, Switch, useLocation, useRouteMatch } from 'react-router-dom';
import { useAuth } from '@dashboard/context/AuthContext/AuthContext';
import { chargeKey } from '@dashboard/lib/queryKeys';
import { fetcher } from '@dashboard/services/request';
import getPriceLabel from '@dashboard/services/translation/getPriceLabel';
import Card from '@dashboard/components/Card';
import CardContent from '@dashboard/components/CardContent';
import CardHeader from '@dashboard/components/CardHeader';
import NavTabs from '@dashboard/components/NavTabs';
import PaymentsBookingsDetail from './scenes/PaymentsBookingsDetail';
import PaymentsBookingsDetailInvoices from './scenes/PaymentsBookingsDetailInvoices';
import PaymentsBookingsDetailEmailLogs from './scenes/PaymentsBookingsDetailEmailLogs';
import PaymentsBookingsDetailAttendees from './scenes/PaymentsBookingsDetailAttendees';
import PaymentsBookingDetailsTransactions from './scenes/PaymentsBookingsDetailTransactions';
import { PaymentStatusBadges } from '@/dashboard/components/PaymentStatusBadges';
import { TrackRecent } from '@dashboard/components/CommandPalette/TrackRecent';
import {
  OrderPageHeader,
  OrderPageSkeleton,
  orderTabSkeleton,
} from '@dashboard/components/OrderPage';
import StatusBadge from '@dashboard/components/StatusBadge';
import useBookingGuests, { guestsToReviewLabel, missingGuestsLabel } from './useBookingGuests';
import { formatDateTime } from '@dashboard/lib/format-date';
import { getPageTitle } from '@dashboard/lib/pageTitle';

const getTabs = ({ base, chargeId, payment }) => [
  {
    name: 'Booking',
    href: `${base}`,
    render: props => <PaymentsBookingsDetail {...props} chargeId={chargeId} payment={payment} />,
  },
  ...(payment.attendeeStatus || payment.tickets?.length > 0
    ? [
        {
          name: 'Attendees',
          href: `${base}/attendees`,
          render: props => <PaymentsBookingsDetailAttendees {...props} payment={payment} />,
        },
      ]
    : []),
  {
    name: 'Emails',
    href: `${base}/emails`,
    render: props => (
      <PaymentsBookingsDetailEmailLogs {...props} chargeId={chargeId} payment={payment} />
    ),
  },
  {
    name: 'Invoices',
    href: `${base}/invoices`,
    render: props => (
      <PaymentsBookingsDetailInvoices {...props} chargeId={chargeId} payment={payment} />
    ),
  },
  {
    name: 'Transactions',
    href: `${base}/transactions`,
    render: props => (
      <PaymentsBookingDetailsTransactions {...props} chargeId={chargeId} payment={payment} />
    ),
  },
];

const PaymentsBookingsDetailOverview = () => {
  const { token, sellerId } = useAuth();
  const {
    url,
    params: { chargeId },
  } = useRouteMatch();
  const location = useLocation();

  const {
    data: payment,
    error,
    isLoading,
  } = useQuery({
    queryKey: chargeKey(chargeId),
    queryFn: () => fetcher(`/sellers/${sellerId}/charges/${chargeId}`, token),
  });
  const { data: guests } = useBookingGuests(payment);
  const missingGuests = missingGuestsLabel(guests);
  const guestsToReview = guestsToReviewLabel(guests);

  if (isLoading) {
    const tab = location.pathname.slice(url.length).split('/')[1];
    return <OrderPageSkeleton tabs={5} {...orderTabSkeleton(tab)} />;
  }

  if (!payment || error) {
    return (
      <Card variant="root" contentColumn>
        <CardContent variant="root">Something went wrong</CardContent>
      </Card>
    );
  }

  const tabs = getTabs({ base: url, chargeId, payment });

  return (
    <Card variant="root" contentColumn>
      <TrackRecent
        type="booking"
        id={payment._id}
        label={getPageTitle(payment.page) || payment._id}
        subtitle={getPriceLabel({ amount: payment.amount, currency: payment.currency })}
        path={`/orders/bookings/${payment._id}`}
      />
      <CardHeader variant="root">
        <OrderPageHeader
          title={payment.orderId ? `Booking #${payment.orderId}` : 'Booking'}
          badges={
            <>
              <PaymentStatusBadges payment={payment} />
              {missingGuests ? <StatusBadge status="unpaid" label={missingGuests} /> : null}
              {guestsToReview ? <StatusBadge status="unpaid" label={guestsToReview} /> : null}
            </>
          }
          date={formatDateTime(payment.createdAt)}
        ></OrderPageHeader>

        <NavTabs aria-label="Booking detail">
          {tabs.map(tab => (
            <NavTabs.Item key={tab.name} to={tab.href} active={location.pathname === tab.href}>
              {tab.name}
            </NavTabs.Item>
          ))}
        </NavTabs>
      </CardHeader>

      <CardContent variant="compact">
        <Switch>
          {tabs.map(tab => (
            <Route key={tab.href} path={tab.href} render={tab.render} exact />
          ))}
          {/* Old addresses still open the Attendees tab. */}
          <Redirect exact from={`${url}/group-registration`} to={`${url}/attendees`} />
          <Redirect exact from={`${url}/tickets`} to={`${url}/attendees`} />
        </Switch>
      </CardContent>
    </Card>
  );
};

export default PaymentsBookingsDetailOverview;
